import { createContext, useContext, type ReactNode } from 'react';

/**
 * LE PORT DES NOTIFICATIONS : ce que le panneau Parametres > Notifications du
 * shell lit et appelle, sans connaitre Firebase. L'hote (`apps/hello`) le
 * fabrique a partir du SDK (`sdk.notifications`) et le fournit par
 * `NotificationsProvider`, comme le port des reglages IA (`iaSettings.tsx`).
 * Les types sont redits ici en vocabulaire d'ecran : `@taki/ui` ne depend pas
 * de `@taki/sdk`.
 */

export type NotificationCanalView = 'email' | 'whatsapp' | 'slack' | 'interne';
export type NotificationRubriqueView = 'citation' | 'lia' | 'message' | 'regle';

/** Le contexte d'une mention (socle conversations). */
export interface NotificationContexteView {
  conversationId: string;
  messageId: string;
  type: 'canal' | 'direct' | 'chatter' | 'page';
  ancre: { type: string; id: string } | null;
  /** Le lieu lisible (« #general », « Direct avec Ada », « Fiche Ada Lovelace »), ou vide. */
  lieu: string;
}
export type NotificationStatutView = 'enFile' | 'envoye' | 'livre' | 'echec' | 'abandonne';
export type NotificationDeclencheurView = 'evenement' | 'planifie' | 'action';
export type NotificationConditionView = 'cree' | 'champChange' | 'statutDevient';

export interface NotificationReglagesView {
  existe: boolean;
  replyTo: string | null;
  nomExpediteur: string | null;
  piedDePage: string | null;
  canaux: { email: { actif: boolean }; whatsapp: { actif: boolean }; slack: { actif: boolean; webhookUrl?: string } };
}

export interface NotificationReglagesPatchView {
  replyTo?: string | null;
  nomExpediteur?: string | null;
  piedDePage?: string | null;
  canaux?: Partial<{ email: boolean; whatsapp: boolean; slack: { actif: boolean; webhookUrl?: string | null } }>;
}

export type NotificationDestinataireView =
  | { kind: 'roles'; roles: Array<'owner' | 'admin' | 'member' | 'viewer'> }
  | { kind: 'membres'; emails: string[] }
  | { kind: 'champ'; champ: string };

export interface NotificationRegleView {
  id: string;
  nom: string;
  actif: boolean;
  declencheur: NotificationDeclencheurView;
  famille: string;
  type?: string;
  condition?: NotificationConditionView;
  champ?: string;
  valeur?: string;
  champDate?: string;
  decalageJours?: number;
  heure?: string;
  gabarit: { sujet: string; corps: string; bouton?: { libelle: string; url: string } };
  destinataires: NotificationDestinataireView[];
  canaux: NotificationCanalView[];
  appId?: string;
  porteId?: string;
}

export type NotificationRegleInputView = Omit<NotificationRegleView, 'id'>;

export type TypeNotificationsExternesView = 'directs' | 'canaux' | 'chatter' | 'mentions' | 'lia' | 'notificationsInternes';
export interface ProfilNotificationsExternesView { actif: boolean; delaiMinutes: number; canaux: Array<'email' | 'whatsapp'>; activeDepuis: number }
export interface NotificationPrefsView {
  mascotteAnimee: boolean;
  notificationsExternes: Partial<Record<TypeNotificationsExternesView, ProfilNotificationsExternesView>>;
  canaux: Partial<Record<NotificationCanalView, boolean>>;
  silence: { de: string; a: string } | null;
  desinscrit: string[];
}

export interface NotificationView {
  id: string;
  canal: NotificationCanalView;
  destinataire: string;
  sujet: string;
  statut: NotificationStatutView;
  derniereErreur: string | null;
  tentatives: number;
  source: { genre: 'regle' | 'action' | 'essai'; ruleId?: string; famille?: string; recordId?: string };
  createdAt: Date | null;
  updatedAt: Date | null;
  /** Ce qui suit est lu par la porte « Notifications » du Journal ; absent des anciens hotes. */
  /** L'adresse e-mail du destinataire quand on la connait (le `destinataire` d'un WhatsApp est un uid). */
  destinataireEmail?: string | null;
  rubrique?: NotificationRubriqueView | null;
  /** Le chemin de l'objet dans l'app (`/app/...`), vide s'il n'y en a pas. */
  chemin?: string;
  objet?: { type: string; id: string } | null;
  contexte?: NotificationContexteView | null;
  /** Livre par le canal console (emulateur sans fournisseur) : SIMULE, rien n'est parti. */
  simule?: boolean;
  prochaineTentative?: Date | null;
  /** Les retours du fournisseur (`delivered`, `bounce`...), dans l'ordre. */
  evenements?: string[];
}

/** Les non lus de la boite interne, comptes par le serveur (G15). */
export interface NonLusView {
  total: number;
  parRubrique: Record<NotificationRubriqueView, number>;
}

/** Une notification de la boite interne (la cloche, le centre). */
export interface NotificationBoiteView {
  id: string;
  rubrique: NotificationRubriqueView;
  sujet: string;
  corps: string;
  /** Le chemin de l'objet dans l'app (`/app/...`), vide s'il n'y en a pas. */
  chemin: string;
  objet: { type: string; id: string } | null;
  /** Present pour une mention (`citation`), ou une reponse de Lia dans une conversation. */
  contexte: NotificationContexteView | null;
  lu: boolean;
  createdAt: Date | null;
}

export interface BilanNotifierView {
  crees: number;
  existants: number;
  ecartes: Array<{ destinataire: string; canal: string; raison: string }>;
}

export interface NotificationsPort {
  preferencesConversations: import('./conversationsCentre.js').PreferenceConversationView[];
  enregistrerPreferenceConversation(p: import('./conversationsCentre.js').PreferenceConversationView): Promise<void>;
  /** Les reglages sont lus. */
  ready: boolean;
  /** Un echec de synchronisation conserve les derniers comptes et remonte sa phrase. */
  erreur?: string | null;
  reglages: NotificationReglagesView;
  prefs: NotificationPrefsView;
  regles: NotificationRegleView[];
  /** Owner ou admin actif : reglages, regles, journal. */
  canManage: boolean;
  /** Owner actif : le seul a allumer WhatsApp. */
  isOwner: boolean;
  enregistrerReglages(patch: NotificationReglagesPatchView): Promise<void>;
  enregistrerPrefs(patch: Partial<NotificationPrefsView>): Promise<void>;
  creerRegle(input: NotificationRegleInputView): Promise<string>;
  modifierRegle(id: string, patch: Partial<NotificationRegleInputView>): Promise<void>;
  supprimerRegle(id: string): Promise<void>;
  journal(limit?: number): Promise<NotificationView[]>;
  /** Les messages de l'org depuis `depuis` (owner, admin), 2000 au plus : la porte du Journal. */
  journalPeriode?(depuis: Date, limite?: number): Promise<NotificationView[]>;
  miens(limit?: number): Promise<NotificationView[]>;
  envoyerEssai(canal?: NotificationCanalView): Promise<BilanNotifierView>;
  testerRegle(ruleId: string, recordId: string): Promise<BilanNotifierView>;
  passage(): Promise<BilanNotifierView & { sweeper?: Record<string, number> }>;
  /** LA BOITE INTERNE de la personne connectee. `nonLus` suit le serveur en direct. */
  nonLus: NonLusView;
  boite(options?: { limit?: number; rubrique?: NotificationRubriqueView }): Promise<NotificationBoiteView[]>;
  marquerLu(id: string): Promise<void>;
  /** A l'ouverture d'une conversation ou d'une fiche : ses notifications deviennent lues. */
  marquerLusDe(objet: { type: string; id: string }): Promise<number>;
  toutMarquerLu(): Promise<number>;
}

const Context = createContext<NotificationsPort | null>(null);

export function NotificationsProvider({ value, children }: { value: NotificationsPort; children: ReactNode }) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useNotificationsPort(): NotificationsPort | null {
  return useContext(Context);
}
